import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AiCaptionServices } from "@/services/bedrock/AiCaptionServices";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { ShareCaption, ShareCaptionStatus } from "./useShareCaption";

/** What the AI gets: the request that made the design, framed as the post this caption goes with. */
export function captionInputFor(pkg: Pick<DesignPackageDetail, "instructions" | "name">): string {
  const request = pkg.instructions?.trim();
  return [
    "Write a social media caption for a post that shares this design.",
    "Write about what the post says to the reader, not about how the image looks.",
    request ? `The design was created from this request: "${request}"` : `The design is called "${pkg.name}".`,
  ].join(" ");
}

export const designCaptionKey = (designPackageId: number, playerId: number) =>
  ["designCaption", designPackageId, playerId] as const;

/**
 * A caption written from the request that made a design, for a design with no prompt caption to
 * start from (a coach posting an unpublished design for a player). Kept per design and player.
 */
export function useDesignCaption(
  design: Pick<DesignPackageDetail, "id" | "instructions" | "name"> | undefined,
  playerId: number | null,
  enabled: boolean
): ShareCaption {
  const queryClient = useQueryClient();
  const active = enabled && !!design && playerId != null;
  const key = designCaptionKey(design?.id ?? -1, playerId ?? -1);

  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      (
        await AiCaptionServices.generate({
          input: captionInputFor(design!),
          designPackageId: design!.id,
          playerId: playerId as number,
        })
      ).caption,
    enabled: active,
    staleTime: Infinity,
    retry: false,
  });

  const rewrite = useCallback(() => {
    if (active) void queryClient.refetchQueries({ queryKey: key, exact: true });
  }, [active, queryClient, key.join("|")]);

  let status: ShareCaptionStatus = "none";
  // Still waiting on the design to write from: already writing, as far as the caption box is concerned.
  if (enabled && playerId != null && !design) status = "pending";
  else if (active) {
    if (query.isFetching) status = "pending";
    else if (query.isError) status = "failed";
    else if (query.data) status = "ready";
    else status = "pending";
  }

  return { caption: query.data ?? null, status, rewrite };
}
